<template>
  <div
    v-if="showPanel"
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4"
  >
    <div class="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] md:max-h-[90vh] overflow-hidden">
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:p-4 border-b gap-3 sm:gap-0"
      >
        <h3 class="text-base md:text-lg font-semibold">Debug Logs</h3>
        <div class="flex flex-wrap gap-2">
          <button
            @click="refreshLogs"
            class="px-2 md:px-3 py-1 bg-blue-500 text-white rounded text-xs md:text-sm"
          >
            🔄 Обновить
          </button>
          <button
            @click="exportLogs"
            class="px-2 md:px-3 py-1 bg-green-500 text-white rounded text-xs md:text-sm"
          >
            📥 Экспорт
          </button>
          <button
            @click="clearLogs"
            class="px-2 md:px-3 py-1 bg-red-500 text-white rounded text-xs md:text-sm"
          >
            🗑️ Очистить
          </button>
          <button
            @click="showPanel = false"
            class="px-2 md:px-3 py-1 bg-gray-500 text-white rounded text-xs md:text-sm"
          >
            ❌ Закрыть
          </button>
        </div>
      </div>

      <div class="p-2 md:p-4 max-h-[70vh] overflow-y-auto">
        <div v-if="logs.length === 0" class="text-gray-500 text-center py-4 md:py-8 text-sm">
          Логи отсутствуют
        </div>

        <div
          v-for="(log, index) in logs"
          :key="index"
          class="mb-2 md:mb-3 p-2 md:p-3 rounded border-l-4"
          :class="getLogClass(log.level)"
        >
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 gap-1">
            <span class="font-semibold text-xs md:text-sm" :class="getLevelClass(log.level)">
              {{ log.level.toUpperCase() }}
            </span>
            <span class="text-xs text-gray-500">
              {{ formatTime(log.timestamp) }}
            </span>
          </div>

          <div class="text-xs md:text-sm font-medium mb-1 break-words">{{ log.message }}</div>

          <div class="text-xs text-gray-600 mb-1 break-all">URL: {{ log.url }}</div>

          <div v-if="log.data" class="text-xs bg-gray-100 p-2 rounded mt-2 overflow-x-auto">
            <pre class="whitespace-pre-wrap break-words">{{
              JSON.stringify(log.data, null, 2)
            }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Кнопка для открытия панели -->
  <button
    v-if="!showPanel && canShow"
    @click="showPanel = true"
    class="fixed bottom-4 left-4 bg-blue-500 text-white p-2 md:p-3 rounded-full shadow-lg z-40 text-sm md:text-base"
  >
    🐛
  </button>
</template>

<script setup lang="ts">
  interface LogEntry {
    timestamp: string;
    level: string;
    message: string;
    data?: any;
    url: string;
  }

  const showPanel = ref(false);
  const logs = ref<LogEntry[]>([]);

  const canShow = computed(() => {
    if (!process.client || !(window as any).__TG_MONITOR__) return false;

    // Показываем только если есть параметр debug=true в URL
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("debug") === "true";
  });

  const refreshLogs = () => {
    if (process.client && (window as any).__TG_MONITOR__) {
      logs.value = (window as any).__TG_MONITOR__.getLogs();
    }
  };

  const exportLogs = () => {
    if (process.client && (window as any).__TG_MONITOR__) {
      (window as any).__TG_MONITOR__.exportLogs();
    }
  };

  const clearLogs = () => {
    if (process.client && (window as any).__TG_MONITOR__) {
      (window as any).__TG_MONITOR__.clearLogs();
      refreshLogs();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getLogClass = (level: string) => {
    switch (level) {
      case "error":
        return "bg-red-50 border-red-400";
      case "warn":
        return "bg-yellow-50 border-yellow-400";
      case "debug":
        return "bg-blue-50 border-blue-400";
      default:
        return "bg-gray-50 border-gray-400";
    }
  };

  const getLevelClass = (level: string) => {
    switch (level) {
      case "error":
        return "text-red-600";
      case "warn":
        return "text-yellow-600";
      case "debug":
        return "text-blue-600";
      default:
        return "text-gray-600";
    }
  };

  // Автоматически загружаем логи при открытии
  watch(showPanel, (newVal) => {
    if (newVal) {
      refreshLogs();
    }
  });

  // Загружаем логи при монтировании
  onMounted(() => {
    if (canShow.value) {
      refreshLogs();
    }
  });
</script>
